import { useCallback, useEffect, useState } from "react";
import { Settings2, X } from "lucide-react";
import { BloomMenu } from "../motion/bloom-menu";
import { ProviderConnectPanel } from "./ProviderConnectPanel";
import { ProviderPickerList } from "./ProviderPickerList";
import { useProviderConnections } from "./hooks/useProviderConnections";
import {
  HARPER_PROVIDER_ACTIVE_CHANGED_EVENT,
  INTERACTIVE_CHAT_PROVIDERS,
  readHiddenChatProviders,
  readInteractiveChatProvider,
  writeActiveHarperProvider,
  type HarperProvider,
} from "./provider-options";

interface FintheonProviderModalProps {
  open: boolean;
  onClose: () => void;
  anchor: HTMLElement | null;
}

export function FintheonProviderModal({
  open,
  onClose,
  anchor,
}: FintheonProviderModalProps) {
  const [provider, setProvider] = useState<HarperProvider>(
    readInteractiveChatProvider,
  );
  const [settingsProvider, setSettingsProvider] =
    useState<HarperProvider | null>(null);
  const providerConnections = useProviderConnections(open);

  const closeMenu = useCallback(() => {
    setSettingsProvider(null);
    onClose();
  }, [onClose]);

  const selectProvider = useCallback(
    (next: HarperProvider) => {
      writeActiveHarperProvider(next);
      setProvider(next);
      closeMenu();
    },
    [closeMenu],
  );

  const handleVisibilityChange = useCallback(
    (changed: HarperProvider, hidden: boolean) => {
      if (!hidden || changed !== provider) return;
      const concealed = new Set(readHiddenChatProviders());
      const fallback = INTERACTIVE_CHAT_PROVIDERS.find(
        (entry) => !concealed.has(entry.id),
      );
      if (!fallback) return;
      writeActiveHarperProvider(fallback.id);
      setProvider(fallback.id);
    },
    [provider],
  );

  useEffect(() => {
    const refresh = () => setProvider(readInteractiveChatProvider());
    window.addEventListener(HARPER_PROVIDER_ACTIVE_CHANGED_EVENT, refresh);
    return () =>
      window.removeEventListener(HARPER_PROVIDER_ACTIVE_CHANGED_EVENT, refresh);
  }, []);

  return (
    <BloomMenu
      open={open}
      onClose={closeMenu}
      anchor={anchor}
      ariaLabel={
        settingsProvider
          ? `Configure ${settingsProvider}`
          : "Choose chat provider"
      }
      role={settingsProvider ? "dialog" : "menu"}
      className="flex min-h-0 flex-col"
    >
      {settingsProvider ? (
        <ProviderSettingsStep
          provider={settingsProvider}
          definitions={providerConnections.definitions}
          onBack={() => setSettingsProvider(null)}
          onSaveKey={providerConnections.saveApiKey}
          onImportDesktop={providerConnections.importDesktop}
          onConnected={() => {
            void providerConnections.refresh();
            setSettingsProvider(null);
          }}
        />
      ) : (
        <ProviderMenuStep
          provider={provider}
          connections={providerConnections.byProvider}
          definitions={providerConnections.definitions}
          loading={providerConnections.loading}
          error={providerConnections.error}
          onClose={closeMenu}
          onSelect={selectProvider}
          onOpenSettings={setSettingsProvider}
          onVisibilityChange={handleVisibilityChange}
          onModelChange={providerConnections.selectModel}
        />
      )}
    </BloomMenu>
  );
}

function ProviderMenuStep(props: {
  provider: HarperProvider;
  connections: ReturnType<typeof useProviderConnections>["byProvider"];
  definitions: ReturnType<typeof useProviderConnections>["definitions"];
  loading: boolean;
  error: string | null;
  onClose: () => void;
  onSelect: (provider: HarperProvider) => void;
  onOpenSettings: (provider: HarperProvider) => void;
  onVisibilityChange: (provider: HarperProvider, hidden: boolean) => void;
  onModelChange: (provider: HarperProvider, model: string) => Promise<void>;
}) {
  return (
    <>
      <div className="flex items-center justify-between px-3 py-2">
        <span className="text-[11px] font-medium text-[var(--fintheon-text)]">
          Providers
        </span>
        <button
          type="button"
          role="menuitem"
          aria-label="Close provider picker"
          onClick={props.onClose}
          className="inline-flex h-7 w-7 items-center justify-center rounded-lg bg-transparent text-[var(--fintheon-muted)] transition-colors hover:text-[var(--fintheon-text)]"
        >
          <X size={14} />
        </button>
      </div>
      <ProviderPickerList
        provider={props.provider}
        connections={props.connections}
        definitions={props.definitions}
        loading={props.loading}
        error={props.error}
        onSelect={props.onSelect}
        onOpenSettings={props.onOpenSettings}
        onVisibilityChange={props.onVisibilityChange}
        onModelChange={props.onModelChange}
      />
    </>
  );
}

function ProviderSettingsStep(props: {
  provider: HarperProvider;
  definitions: ReturnType<typeof useProviderConnections>["definitions"];
  onBack: () => void;
  onSaveKey: ReturnType<typeof useProviderConnections>["saveApiKey"];
  onImportDesktop: ReturnType<typeof useProviderConnections>["importDesktop"];
  onConnected: () => void;
}) {
  return (
    <div className="min-h-0">
      <div className="flex items-center gap-2 px-3 py-2 text-[11px] text-[var(--fintheon-muted)]">
        <Settings2 size={13} /> Provider connection
      </div>
      <ProviderConnectPanel
        provider={props.provider}
        definitions={props.definitions}
        onBack={props.onBack}
        backLabel="Providers"
        onSaveKey={props.onSaveKey}
        onImportDesktop={props.onImportDesktop}
        onConnected={props.onConnected}
      />
    </div>
  );
}
